import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { Upload, Download, MessageCircle, Phone, RefreshCw, CheckCircle, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const LOAN_TYPE_LABELS = {
  credit_card: 'Credit Card', personal_loan: 'Personal Loan',
  consumer_loan: 'Consumer Loan', business_loan: 'Business Loan',
  unsecured_business_loan: 'Unsecured Business Loan', home_loan: 'Home Loan',
  auto_loan: 'Auto Loan', gold_loan: 'Gold Loan',
  lap: 'Loan Against Property', education_loan: 'Education Loan', other_loan: 'Other'
};

function fmt(n) { return '₹' + (parseInt(n) || 0).toLocaleString('en-IN'); }

export default function ProposalTab({ lead, onUpdate }) {
  const [analysis, setAnalysis] = useState(lead.creditAnalysis || null);
  const [lenders, setLenders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [fee, setFee] = useState('');
  const [tenure, setTenure] = useState('6-8 months');
  const [step, setStep] = useState(analysis ? 2 : 1); // 1=upload, 2=review, 3=done
  const fileRef = useRef();
  const { user } = useAuth();
  const token = localStorage.getItem('crm_token');

  useEffect(() => {
    if (lead.proposalGenerated) {
      setStep(3);
      setGenerated({ downloadUrl: `/api/proposals/download/${lead.id}/${lead.proposalFile}` });
    } else if (analysis) {
      setLenders((analysis.accounts || []).filter(a => (a.secured === 'no' || a.secured === 'unknown') && parseInt(a.balance) >= 25000));
      setStep(2);
    }
  }, [lead, analysis]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append(file.type === 'application/pdf' ? 'pdf' : 'images', file);
      const r = await api.post(`/proposals/extract/${lead.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setAnalysis(r.data);
      const accs = (r.data.accounts || []).filter(a => (a.secured === 'no' || a.secured === 'unknown') && parseInt(a.balance) >= 25000);
      setLenders(accs);
      setStep(2);
      toast.success(`${r.data.accounts?.length || 0} accounts extracted!`);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Extraction failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleLender = (idx) => {
    setLenders(prev => prev.map((l, i) => i === idx ? { ...l, _excluded: !l._excluded } : l));
  };

  const includedLenders = lenders.filter(l => !l._excluded);
  const totalOutstanding = includedLenders.reduce((s, l) => s + (parseInt(l.balance) || 0), 0);

  const openGoogleForm = () => {
    const today = new Date().toISOString().split('T')[0];
    const consultantName = user?.name || '';
    const clientName = lead.name || '';
    const clientPhone = lead.mobile || '';
    const clientEmail = lead.email || '';
    const clientCity = lead.city || '';

    // Lender entry IDs from Google Form (up to 25 lenders)
    const LENDER_ENTRIES = [
      { bank: 'entry.1099680185', type: 'entry.661795219', acc: 'entry.1911664672', amount: 'entry.1136147286' },
      { bank: 'entry.219370359',  type: 'entry.1077859656', acc: 'entry.2100078547', amount: 'entry.947209151' },
      { bank: 'entry.843111448',  type: 'entry.1220673749', acc: 'entry.2074722978', amount: 'entry.30782660' },
      { bank: 'entry.1771942848', type: 'entry.473506518',  acc: 'entry.2079620943', amount: 'entry.1122779940' },
      { bank: 'entry.276602917',  type: 'entry.731719164',  acc: 'entry.1966376599', amount: 'entry.624352927' },
      { bank: 'entry.1391413342', type: 'entry.2087554450', acc: 'entry.1342502123', amount: 'entry.725133677' },
      { bank: 'entry.1621106676', type: 'entry.1273538579', acc: 'entry.1769920374', amount: 'entry.679634626' },
      { bank: 'entry.772449912',  type: 'entry.332419648',  acc: 'entry.3307845',    amount: 'entry.1567038992' },
      { bank: 'entry.308036461',  type: 'entry.1718522183', acc: 'entry.2120547105', amount: 'entry.782552429' },
      { bank: 'entry.1357300830', type: 'entry.1970643233', acc: 'entry.1976719325', amount: 'entry.812354932' },
      { bank: 'entry.350154054',  type: 'entry.1841646716', acc: 'entry.1105798136', amount: 'entry.2029382041' },
      { bank: 'entry.756537482',  type: 'entry.2065252352', acc: 'entry.1781894023', amount: 'entry.1344588491' },
      { bank: 'entry.57341195',   type: 'entry.1228345019', acc: 'entry.655126368',  amount: 'entry.520172700' },
      { bank: 'entry.376612387',  type: 'entry.1245834317', acc: 'entry.1027377586', amount: 'entry.826065300' },
      { bank: 'entry.1743489064', type: 'entry.1794641020', acc: 'entry.1272075242', amount: 'entry.1411311525' },
      { bank: 'entry.1424425341', type: 'entry.1300488675', acc: 'entry.1452177471', amount: 'entry.1413656842' },
      { bank: 'entry.845709099',  type: 'entry.1830832439', acc: 'entry.43685020',   amount: 'entry.731228798' },
      { bank: 'entry.1449231219', type: 'entry.1737937288', acc: 'entry.1039651764', amount: 'entry.1412137110' },
      { bank: 'entry.2016607487', type: 'entry.2119952722', acc: 'entry.1769828887', amount: 'entry.839097090' },
      { bank: 'entry.2065731663', type: 'entry.1034648363', acc: 'entry.683768991',  amount: 'entry.1339776020' },
      { bank: 'entry.1534957165', type: 'entry.2130198467', acc: 'entry.114811748',  amount: 'entry.2033427689' },
      { bank: 'entry.1675879143', type: 'entry.935657555',  acc: 'entry.1265870305', amount: 'entry.1409657644' },
      { bank: 'entry.1402071275', type: 'entry.1643444285', acc: 'entry.265273407',  amount: 'entry.716441289' },
      { bank: 'entry.1434043333', type: 'entry.1099113431', acc: 'entry.1697106130', amount: 'entry.1410381537' },
      { bank: 'entry.1888336045', type: 'entry.1834488205', acc: 'entry.784561653',  amount: 'entry.1887851280' },
    ];

    const LOAN_TYPE_LABELS_FORM = {
      credit_card: 'CREDIT CARD', personal_loan: 'PERSONAL LOAN',
      consumer_loan: 'CONSUMER LOAN', business_loan: 'BUSINESS LOAN',
      unsecured_business_loan: 'UNSECURED BUSINESS LOAN',
      home_loan: 'HOME LOAN', auto_loan: 'AUTO LOAN',
      gold_loan: 'GOLD LOAN', lap: 'LOAN AGAINST PROPERTY',
      education_loan: 'EDUCATION LOAN', other_loan: 'OTHER'
    };

    const BASE_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdLoNhHETkmjfhQ-v3uvF_yKWXBeY4UFIbCojqDq8eAxmOJdA/viewform';

    let params = new URLSearchParams({
      'usp': 'pp_url',
      'entry.1261814352': consultantName,
      'entry.611806054': today,
      'entry.66586022': clientEmail,
      'entry.1131953817': clientPhone,
      'entry.883061386': clientCity,
      'entry.2067926277': clientName,
      'entry.689791028': fee || '',
    });

    // Add lenders
    includedLenders.forEach((l, i) => {
      if (i >= LENDER_ENTRIES.length) return;
      const e = LENDER_ENTRIES[i];
      params.append(e.bank, (l.lender || '').toUpperCase());
      params.append(e.type, LOAN_TYPE_LABELS_FORM[l.loan_type] || (l.loan_type || '').toUpperCase());
      params.append(e.acc, l.account_no || '');
      params.append(e.amount, String(parseInt(l.balance) || 0));
    });

    window.open(`${BASE_URL}?${params.toString()}`, '_blank');
  };

  const generate = async () => {
    if (!fee) return toast.error('Fee required hai');
    setGenerating(true);
    try {
      const r = await api.post(`/proposals/generate/${lead.id}`, {
        lenders: includedLenders,
        fee,
        tenure,
        consultantName: user?.name,
        consultantPhone: user?.phone || '0120-4894731'
      });
      setGenerated(r.data);
      setStep(3);
      toast.success('Proposal generated!');
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const whatsappText = generated?.whatsappText || `Dear ${lead.name},\n\nYour debt settlement proposal is ready. Please contact Easy Settlement for details.\n\n0120-4894731`;
  const waLink = `https://wa.me/91${lead.mobile}?text=${encodeURIComponent(whatsappText)}`;

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
        {[
          { n: 1, label: 'Credit Report' },
          { n: 2, label: 'Review & Configure' },
          { n: 3, label: 'Proposal Ready' }
        ].map((s, i) => (
          <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px',
              background: step >= s.n ? 'var(--navy)' : 'var(--surface)',
              borderRadius: i === 0 ? '8px 0 0 8px' : i === 2 ? '0 8px 8px 0' : '0',
              flex: 1
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: step >= s.n ? 'var(--gold-light)' : 'var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
                color: step >= s.n ? 'var(--navy)' : 'var(--text-muted)',
                flexShrink: 0
              }}>{step > s.n ? '✓' : s.n}</div>
              <span style={{ fontSize: 12, fontWeight: 600, color: step >= s.n ? 'var(--gold-light)' : 'var(--text-muted)' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* STEP 1: Upload */}
      {step === 1 && (
        <div>
          <div
            style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: 40, textAlign: 'center', cursor: 'pointer', background: 'var(--surface)' }}
            onClick={() => fileRef.current.click()}
          >
            <input ref={fileRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files[0])} />
            {uploading ? (
              <>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Analyzing credit report... (30-60 seconds)</div>
              </>
            ) : (
              <>
                <Upload size={32} style={{ color: 'var(--text-light)', marginBottom: 12 }} />
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Upload Credit Report</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>PDF ya Screenshot — CIBIL, Experian, CRIF, Equifax</div>
                <div className="btn btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>
                  📄 Select File
                </div>
              </>
            )}
          </div>

          {lead.creditReportUploaded && analysis && (
            <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={16} style={{ color: 'var(--success)' }} />
              <span>Previous analysis available — <button className="btn btn-ghost btn-sm" onClick={() => { setLenders((analysis.accounts || []).filter(a => (a.secured === 'no' || a.secured === 'unknown') && parseInt(a.balance) >= 25000)); setStep(2); }}>Use saved analysis</button></span>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Review lenders + configure */}
      {step === 2 && analysis && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Review Lenders</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {includedLenders.length} lenders selected · Total: {fmt(totalOutstanding)}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>
              <RefreshCw size={13} /> Re-upload
            </button>
          </div>

          {/* Lenders table */}
          <div className="card" style={{ padding: 0, marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ padding: '8px 12px', fontSize: 11, textAlign: 'left', fontWeight: 700 }}>Include</th>
                  <th style={{ padding: '8px 12px', fontSize: 11, textAlign: 'left', fontWeight: 700 }}>Lender</th>
                  <th style={{ padding: '8px 12px', fontSize: 11, textAlign: 'left', fontWeight: 700 }}>Type</th>
                  <th style={{ padding: '8px 12px', fontSize: 11, textAlign: 'right', fontWeight: 700 }}>Balance</th>
                  <th style={{ padding: '8px 12px', fontSize: 11, textAlign: 'right', fontWeight: 700 }}>Overdue</th>
                  <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700 }}>Secured</th>
                </tr>
              </thead>
              <tbody>
        {lenders.map((lender, idx) => {
          const lenderName = lender.lender || lender.name;
          return (
                  <tr key={idx} style={{ background: lender._excluded ? '#fef2f2' : idx % 2 === 0 ? 'var(--surface)' : 'white', opacity: lender._excluded ? 0.6 : 1 }}>
                    <td style={{ padding: '8px 12px' }}>
                      <input type="checkbox" checked={!lender._excluded} onChange={() => toggleLender(idx)} style={{ width: 16, height: 16 }} />
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13 }}>{lenderName}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{LOAN_TYPE_LABELS[lender.loan_type] || lender.loan_type}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <input
                        type="number"
                        value={lender.balance}
                        onChange={e => setLenders(prev => prev.map((l, i) => i === idx ? { ...l, balance: e.target.value } : l))}
                        style={{ width: 110, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 13, fontWeight: 700, color: 'var(--navy)', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right', color: lender.overdue > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{lender.overdue > 0 ? fmt(lender.overdue) : '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span className={`badge ${lender.secured === 'yes' ? 'badge-red' : lender.secured === 'no' ? 'badge-green' : 'badge-yellow'}`}>
                        {lender.secured === 'yes' ? '🔒 Secured' : lender.secured === 'no' ? '✓ Unsecured' : '? Unknown'}
                      </span>
                    </td>
                  </tr>
          );
        })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--navy)' }}>
                  <td colSpan={3} style={{ padding: '10px 12px', color: 'var(--gold-light)', fontWeight: 700, fontSize: 13 }}>
                    Total ({includedLenders.length} lenders)
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--gold-light)', fontWeight: 800, fontSize: 14, textAlign: 'right' }}>{fmt(totalOutstanding)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Fee and tenure */}
          <div className="grid grid-2" style={{ gap: 12, marginBottom: 16 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Enrollment Fee (₹) *</label>
              <input className="form-control" type="number" value={fee} onChange={e => setFee(e.target.value)} placeholder="e.g. 15000" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Tenure</label>
              <select className="form-control" value={tenure} onChange={e => setTenure(e.target.value)}>
                {['3-6 months', '6-8 months', '8-12 months', '12-18 months', '18-24 months'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Add manual lender */}
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              const name = window.prompt('Lender name:');
              if (!name) return;
              const amount = window.prompt('Outstanding amount (₹):');
              if (!amount) return;
              setLenders(prev => [...prev, {
                lender: name, loan_type: 'other_loan', loan_type_label: 'Other',
                balance: parseInt(amount) || 0, overdue: 0, secured: 'no',
                account_no: '', _manual: true
              }]);
            }}>
              + Add Lender Manually
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={generate} disabled={generating || includedLenders.length === 0 || !fee} style={{ flex: 1 }}>
                {generating ? <><span className="spinner" style={{ marginRight: 8 }} />Generating PDF...</> : '📄 Generate Proposal PDF'}
              </button>
              <button
                className="btn"
                style={{ background: '#4285F4', color: 'white', borderRadius: 8, padding: '10px 16px', fontWeight: 600, fontSize: 13, cursor: includedLenders.length === 0 ? 'not-allowed' : 'pointer', opacity: includedLenders.length === 0 ? 0.5 : 1 }}
                disabled={includedLenders.length === 0}
                onClick={() => openGoogleForm()}
              >
                📋 Fill Form
              </button>
            </div>

            {/* WhatsApp & Email share */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn"
                style={{ flex: 1, background: '#25d366', color: 'white', borderRadius: 8, padding: '10px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                onClick={async () => {
                  const msg = `Dear ${lead.name},\n\nWe have prepared your Debt Settlement Analysis:\n\nTotal Outstanding: ₹${totalOutstanding.toLocaleString('en-IN')}\nLenders: ${includedLenders.length}\nEnrollment Fee: ₹${fee || 'TBD'}\n\nPlease contact us to proceed.\n\nEasy Settlement\n📞 0120-4894731`;
                  try {
                    await api.post('/whatsapp/send', { leadId: lead.id, message: msg });
                    toast.success('WhatsApp message sent via Wazzup!');
                    api.post('/proposals/mark-shared/' + lead.id, { type: 'analysis' }).catch(() => {});
                  } catch (e) { toast.error(e.response?.data?.error || 'WhatsApp failed'); }
                }}
              >
                💬 Send on WhatsApp
              </button>
              <button
                className="btn"
                style={{ flex: 1, background: 'var(--navy)', color: 'white', borderRadius: 8, padding: '10px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    await api.post(`/emails/send-day/${lead.id}`, { day: 2 });
                    toast.success('Analysis reminder email sent!');
                    api.post('/proposals/mark-shared/' + lead.id, { type: 'analysis' }).catch(() => {});
                  } catch (e) { toast.error(e.response?.data?.error || 'Email failed'); }
                }}
              >
                📧 Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Proposal ready */}
      {step === 3 && (
        <div>
          <div style={{ textAlign: 'center', padding: '20px 0 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>Proposal Ready!</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Generated {lead.proposalGeneratedAt ? new Date(lead.proposalGeneratedAt).toLocaleDateString('en-IN') : 'just now'}
              {lead.proposalFee && ` · Fee: ${fmt(lead.proposalFee)}`}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Download */}
            <a
              href={`${API_URL}/proposals/download/${lead.id}/${lead.proposalFile}?token=${token}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none' }}
            >
              <Download size={16} /> Download Proposal PDF
            </a>

            {/* WhatsApp */}
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#25d366', color: 'white', borderRadius: 8, padding: '10px 16px', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}
              onClick={() => {
                api.post(`/proposals/mark-shared/${lead.id}`, { type: 'proposal' }).catch(() => {});
              }}
            >
              <MessageCircle size={16} /> Send on WhatsApp
            </a>

            {/* Call */}
            <a
              href={`tel:${lead.mobile}`}
              className="btn btn-ghost"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none' }}
            >
              <Phone size={16} /> Call {lead.name}
            </a>

            {/* Email placeholder */}
            <button className="btn btn-ghost" style={{ opacity: 0.5 }} disabled>
              📧 Email (coming soon)
            </button>
          </div>

          {/* Regenerate */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setStep(analysis ? 2 : 1); setGenerated(null); }}>
              🔄 Regenerate Proposal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
